import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { InstantSearch } from 'react-instantsearch'

import Splash from './components/Splash/Splash'
import { algolia_client } from './utils/AlgoliaSearch'
import { getOrgById, retrieveId } from './utils/getOrgById'
import ProductsDialog from './components/PricingPlansTable/ProductsDialog'
import {
  setCreditTopOffModalOpen,
  setProductsModalOpen,
  setSettingsModalDefaultTab,
  setSettingsModalOpen,
} from './Redux/appStateSlice'
import { MediaAreaPage } from './views/MediaArea'
import { fetchUserRoles } from './Redux/userSlice'
import SessionTrackingProvider from './context/SessionTrackingContext'
import { BulkImportModal } from './components/BulkImportModal/BulkImportModal'
import CreditTopOffModal from './components/CreditTopOffModal'
import UniversalSettingsModal from './components/UniversalSettingsModal'
import { ProtectedRoute } from './components/ProtectedRoute'
import { InternalRoute } from './components/Internal/InternalRoute'
// import TestProfiencyReports from './views/TestProfiencyReports'

const AcceptTeamInvitation = React.lazy(() => import('./views/Team/invitations/AcceptTeamInvitation'))
const AcceptOrganizationInvitation = React.lazy(() => import('./views/Organization/AcceptOrganizationInvitation'))

const InternalOrgManager = React.lazy(() => import('./components/Internal'))

const ActivityView = React.lazy(() => import('./views/NewAssignment/ActivityView'))
const BuilderView = React.lazy(() => import('./views/NewBuilder/BuilderView'))
const AssignmentAnalyticsPage = React.lazy(() => import('./views/AssignmentAnalytics'))
const Onboarding = React.lazy(() => import('./views-auth/Onboarding/index'))
const Login = React.lazy(() => import('./views-auth/Login/index'))
const CreateAccount = React.lazy(() => import('./views-auth/CreateAccount'))
const JoinCourse = React.lazy(() => import('./views-auth/JoinCourse'))
const SettingsPage = React.lazy(() => import('./views/Settings/index'))
const CleverAuth = React.lazy(() => import('./views-auth/CleverAuth'))
const Library = React.lazy(() => import('./views/Library'))
const Gradebook = React.lazy(() => import('./views/Gradebook'))
const ThankYou = React.lazy(() => import('./views/ThankYou'))
const SetPreviewPage = React.lazy(() => import('./views/SetPreview'))
const SectionsPage = React.lazy(() => import('./views/Sections'))
const SectionAnalytics = React.lazy(() => import('./views/SectionAnalytics'))
const AppLayout = React.lazy(() => import('./AppLayout'))
const Discover = React.lazy(() => import('./views/DiscoverSets'))
const Home = React.lazy(() => import('./views/Home'))
const Support = React.lazy(() => import('./views/Support'))
const Classrooms = React.lazy(() => import('./views/Classrooms'))
const LtkiAuth = React.lazy(() => import('./views-auth/LTI/LtikAuth'))
const LTIContentSelecttion = React.lazy(() => import('./views/LTIContentSelection'))
const Rubric = React.lazy(() => import('./views/Rubric'))
const Team = React.lazy(() => import('./views/Team'))
const Organization = React.lazy(() => import('./views/Organization'))
const GoogleClassroomAuth = React.lazy(() => import('./views-auth/GoogleClassroom/GoogleClassroomAuth'))

function App() {
  const dispatch = useDispatch()
  const { authLoaded, profileLoaded, auth } = useSelector(({ user }) => user)
  const loading = !(authLoaded && profileLoaded)
  const location = useLocation()

  const {
    productsModalOpen = false,
    highlightedPlan,
    creditTopOffModalOpen,
    settingsModalOpen,
    settingsModalDefaultTab,
  } = useSelector(({ app }) => app)

  React.useEffect(() => {
    if (auth && auth.uid && (location.pathname?.includes('school') || location.pathname?.includes('district'))) {
      dispatch(fetchUserRoles({ email: auth?.email, pathname: location.pathname }))
    }
  }, [auth, dispatch, location.pathname])

  return (
    <SessionTrackingProvider userId={auth?.uid}>
      {loading ? (
        <Splash />
      ) : (
        <>
          <BulkImportModal />
          <CreditTopOffModal
            open={creditTopOffModalOpen}
            subtitle={settingsModalOpen ? 'Purchase credits to top off your account' : undefined}
            title={settingsModalOpen ? 'Purchase Top-Off Credits' : undefined}
            onClose={() => dispatch(setCreditTopOffModalOpen(false))}
          />
          {/* <ProductsDialog
            highlightedPlan={highlightedPlan}
            open={productsModalOpen}
            onClose={() => dispatch(setProductsModalOpen(false))}
          /> */}
          <UniversalSettingsModal
            defaultTab={settingsModalDefaultTab}
            open={settingsModalOpen}
            onClose={() => {
              dispatch(setSettingsModalOpen(false))
              dispatch(setSettingsModalDefaultTab(0))
            }}
          />
          <AppRoutes />
        </>
      )}
    </SessionTrackingProvider>
  )
}

export default App

function AppRoutes() {
  return (
    <Routes>
      {/* is not Authenticated */}
      <Route element={<JoinCourse />} path="/join/:id" />
      <Route element={<MediaAreaPage />} path="/media-area/:id" />
      <Route element={<CleverAuth />} path="/clever" />
      {/* <Route element={<CleverAuthMobile />} path="/clever-mobile" /> */}
      <Route element={<LtkiAuth />} path="/lti" />
      <Route element={<LtkiAuth isDeeplinking />} path="/lti-deeplinking" />
      <Route element={<AcceptTeamInvitation />} path="/teams/:teamId/accept-invite" />
      <Route element={<AcceptOrganizationInvitation />} path="/organization/:organizationId/accept-invite" />

      <Route element={<PublicOutlet />} path="/auth">
        <Route element={<Login />} path="/auth/login" />
        <Route element={<CreateAccount />} path="/auth/create-account" />
        <Route element={<Navigate to="/auth/login" />} path="*" />
      </Route>

      {/* <Route element={<TestProfiencyReports />} path="/test-proficiency-reports" /> */}
      {/* Activity Preview Pages */}
      <Route element={<SetPreviewPage />} path="/preview/:id" />
      <Route element={<ActivityView />} path="/activity/:id" />

      <Route element={<PrivateOutlet />} path="/">
        {/* Google Classroom OAuth Callback Route - accessible to all authenticated users */}
        <Route element={<GoogleClassroomAuth />} path="/auth/google-classroom" />

        {/* is Authenticated and has profile */}
        <Route element={<MainNavigationOutlet />} path="/">
          <Route element={<Home />} path="/" />
          <Route element={<Discover />} path="/d" />
          <Route element={<LTIContentSelecttion />} path="/lti-content-selection" />
          <Route element={<SectionsPage />} path="/s/:id" />
          <Route element={<SectionAnalytics />} path="/s/:id/analytics" />
          <Route element={<Gradebook />} path="/g" />
          {/* <Route element={<Support />} path="/support" /> */}
          <Route element={<Classrooms />} path="/classrooms" />

          <Route element={<Library />} path="/l" />
          <Route element={<AssignmentAnalyticsPage />} path="/a/:id" />
          <Route element={<SettingsPage />} path="/settings" />
          <Route element={<ActivityView />} path="/assignment/:id" />
          <Route element={<ThankYou />} path="/thank-you" />
          <Route element={<Rubric />} path="/rubric/:id" />
          <Route element={<BuilderView />} path="/b/:setId" />

          <Route element={<Navigate to="/" />} path="*" />

          <Route element={<ProtectedRoute allowedRoles={['ADMIN']} />}>
            <Route element={<Team />} path="/team/:teamId/*" />
            <Route element={<Organization />} path="/organization/:organizationId/*" />
          </Route>
        </Route>

        {/* Internal Tools - Restricted to internal users */}
        <Route element={<InternalRoute />}>
          <Route element={<InternalOrgManager />} path="/internal/*" />
        </Route>

        {/* is Authenticated and does not have profile - Teacher/Student Onboarding */}
        <Route element={<Onboarding />} path="/onboarding" />
      </Route>
    </Routes>
  )
}

function PrivateOutlet() {
  const auth = useSelector(({ user }) => user.auth)
  const profile = useSelector(({ user }) => user.profile)
  const location = useLocation()

  if (!auth?.uid) {
    const redirectUrl = encodeURIComponent(location.pathname + location.search)

    return <Navigate to={`/auth/login?redirect=${redirectUrl}`} />
  }
  if (profile?.roles && location.pathname === '/onboarding') {
    // Check for redirect parameter and preserve it
    const redirectParam = new URLSearchParams(location.search).get('redirect')

    if (redirectParam) {
      try {
        const decodedRedirect = decodeURIComponent(redirectParam)

        return <Navigate to={decodedRedirect} />
      } catch (error) {
        console.error('Error decoding redirect parameter:', error)

        return <Navigate to="/" />
      }
    }

    return <Navigate to="/" />
  }

  return <Outlet />
}
function AdminRoute() {
  const { adminOrgs, managerOrgs } = useSelector(({ organization }) => organization)
  const { orgId } = useParams()
  const formattedOrgId = retrieveId(orgId)
  const adminOrgObj = getOrgById(adminOrgs || [], formattedOrgId)
  const managerOrgObj = getOrgById(managerOrgs || [], formattedOrgId)
  const isAllowed = adminOrgObj || managerOrgObj
  const isManager = managerOrgObj?.isManager

  if (!isAllowed) {
    return <Navigate to="/" />
  }

  if (isManager) {
    const path = window.location.pathname

    if (path.includes('/teacher/')) {
      return <Navigate to="/" />
    }
  }

  return <Outlet />
}

function PublicOutlet() {
  const { auth } = useSelector(({ user }) => user)

  if (auth?.uid) {
    return <Navigate to="/" />
  }

  return <Outlet />
}

const foucsRoutes = [
  'b',
  'assignment',
  'activity',
  'lti-content-selection',
  'checkpoint',
  'rubric',
  'free-org-trial',
  // 'organization',
]

function MainNavigationOutlet() {
  const profile = useSelector(({ user }) => user.profile)
  const location = useLocation()
  const route = location.pathname.split('/')[1]
  const path = window.location.pathname

  const indexName = 'speakable-sets-live'
  const myCreationsIndex = 'my creations index'

  // if (profile?.roles && foucsRoutes.includes(route)) {
  //   return <Outlet />
  // }
  if (!profile?.roles) {
    return <Navigate to="/onboarding" />
  }

  return (
    <InstantSearch
      routing
      future={{
        preserveSharedStateOnUnmount: true,
      }}
      indexName={path === '/l' ? myCreationsIndex : indexName}
      searchClient={algolia_client}
    >
      {profile.roles && foucsRoutes.includes(route) ? <Outlet /> : <AppLayout />}
    </InstantSearch>
  )
}
